import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDebounce } from "react-use";
import styled from "styled-components";
import { useRecentSearches } from "../../hooks/useRecentSearches";
import { RecentSearchesDropdown } from "./RecentSearches";

const SearchContainer = styled.div`
    position: relative;
    
    input {
        border-radius: 50px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: inset 1px 1px 8px rgba(0, 0, 0, 0.1);
        background-color: rgba(255, 255, 255, 0.5);
        transition: all ease-in-out 0.3s;
        padding: 0 10px;
        font-size: 12px;
        height: 40px;
        width: 450px;
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

    input::placeholder {
        color: #888 !important;
    }

    input:focus {
        border: 1px solid #fff;
        box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.2);
        background-color: #fff;
        outline: none;
        color: #000;
        border-radius: 0px 50px 0 0;
    }
`;

interface SearchInputProps {
    /** Current value for the search input (controlled) */
    value?: string;
    /** Placeholder text for the search input */
    placeholder?: string;
    /** Callback fired when search term changes (debounced) */
    onSearchChange?: (searchTerm: string) => void;
    /** Callback fired when user submits search (Enter key or selecting recent search) */
    onSearchSubmit?: (searchTerm: string) => void;
    /** Debounce delay in milliseconds for search changes */
    debounceMs?: number;
    /** Custom styling className */
    className?: string;
}

export const SearchInput: React.FC<SearchInputProps> = ({
    value = "",
    placeholder = "Suche...",
    onSearchChange,
    onSearchSubmit,
    debounceMs = 300,
    className
}) => {
    const [searchQuery, setSearchQuery] = useState(value);
    const [showRecentSearches, setShowRecentSearches] = useState(false);
    
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const { recentSearches, addRecentSearch, clearRecentSearches, removeRecentSearch } = useRecentSearches();

    const skipNextDebounceRef = useRef(false);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        if (value !== searchQuery) {
            skipNextDebounceRef.current = true;
            setSearchQuery(value);
            if (searchInputRef.current) {
                searchInputRef.current.value = value;
            }
        }
    }, [value]);

    // Debounce search query changes
    useDebounce(
        () => {
            const trimmedQuery = searchQuery.trim();
            if (trimmedQuery.length === 0) {
                return;
            }
            if (skipNextDebounceRef.current || !isFocusedRef.current) {
                skipNextDebounceRef.current = false;
                return;
            }
            if (onSearchChange) {
                onSearchChange(trimmedQuery);
            }
        },
        debounceMs,
        [searchQuery, onSearchChange, debounceMs]
    );


    // Handle clicks outside search dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                // Save search if 3+ characters when clicking outside
                if (searchQuery.trim().length >= 3) {
                    addRecentSearch(searchQuery.trim());
                }
                setShowRecentSearches(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchQuery, addRecentSearch]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowRecentSearches(false);
        const nextValue = event.target.value;
        setSearchQuery(nextValue);

        if (nextValue.trim().length === 0 && onSearchChange) {
            onSearchChange("");
        }
    };

    const handleFocus = () => {
        isFocusedRef.current = true;
        if (recentSearches.length > 0) {
            setShowRecentSearches(true);
        }
    };

    const handleBlur = () => {
        isFocusedRef.current = false;
        // Save to recent searches when user leaves input with 3+ characters
        if (searchQuery.trim().length >= 3) {
            addRecentSearch(searchQuery.trim());
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && searchQuery.trim()) {
            // Save to recent searches when user presses Enter
            addRecentSearch(searchQuery.trim());
            if (onSearchChange) {
                onSearchChange(searchQuery.trim());
            }
            if (onSearchSubmit) {
                onSearchSubmit(searchQuery.trim());
            }
        }
    };

    const handleSearchSelect = useCallback((query: string) => {
        setSearchQuery(query);
        addRecentSearch(query); // Save when user selects from recent searches
        if (searchInputRef.current) {
            searchInputRef.current.value = query;
        }
        if (onSearchChange) {
            onSearchChange(query);
        }
        if (onSearchSubmit) {
            onSearchSubmit(query);
        }
    }, [addRecentSearch, onSearchChange, onSearchSubmit]);

    const handleSearchRemove = useCallback((query: string) => {
        removeRecentSearch(query);
    }, [removeRecentSearch]);

    const handleClearAll = useCallback(() => {
        clearRecentSearches();
        setShowRecentSearches(false);
    }, [clearRecentSearches]);

    return (
        <SearchContainer 
            ref={searchContainerRef}
            className={`${showRecentSearches ? "dropdown-open" : ""} ${className || ""}`}
        >
            <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                type="search"
            />
            <RecentSearchesDropdown
                recentSearches={recentSearches}
                isVisible={showRecentSearches}
                onSearchSelect={handleSearchSelect}
                onSearchRemove={handleSearchRemove}
                onClearAll={handleClearAll}
            />
        </SearchContainer>
    );
};
